import { Component } from "@angular/core";

import { GridColumnViewSettings, IGridColumn, IGridSettings, ITableSettings } from "@features/grid";


@Component({
    selector: "widget-html-syntax-tooltip",
    templateUrl: "./widget-html-syntax-tooltip.component.html",
    styleUrls: ["./widget-html-syntax-tooltip.component.scss"]
})
export class WidgetHtmlSyntaxTooltipComponent {
    syntaxGridSettings: IGridSettings = <IGridSettings>{ readonly: true, sortEnabled: false };

    syntaxColumns: IGridColumn[] = <IGridColumn[]>[
        {
            field: "syntax", header: "Syntax",
            viewSettings: new GridColumnViewSettings({ width: "35%" })
        },
        {
            field: "example", header: "Example",
            viewSettings: new GridColumnViewSettings({ width: "35%" })
        },
        { field: "description", header: "Description" }
    ];

    syntaxComponentsColumns: IGridColumn[] = <IGridColumn[]>[
        {
            field: "component", header: "Component",
            viewSettings: new GridColumnViewSettings({ width: "40%" })
        },
        { field: "description", header: "Description" }
    ];

    querySyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@query(query_name, column?)",
                example: "<p>Welcome, @query(getUserDetails, username)!</p>",
                description: "Full syntax with column specified."
            },
            {
                syntax: "@query(query_name)",
                example: "<p>The price of the product is $@query(getProductPriceInfo).</p>",
                description: "Reduced syntax. In this syntax the column is not specified which means " +
                    "the first column of the query will be automatically chosen to retrieve data."
            },
            {
                syntax: "@query(query_name, column?, take?, skip?)",
                example: "<p>First 10 results: @query(getUsers, name, 10, 0)</p>",
                description: "Full syntax with pagination. Optional parameters: column, take (limit), skip (offset)."
            },
        ],
        paginator: false
    };

    querySyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@query",
                description: "This is the directive name indicating that the following arguments " +
                    "are for a dynamic SQL query insertion."
            },
            {
                component: "query_name",
                description: "This represents the name of the SQL query that you have defined in your application. " +
                    "It specifies which query to pull data from. Required parameter."
            },
            {
                component: "column",
                description: "This is the specific column in the query's result set from which " +
                    "you want to retrieve data. Optional parameter."
            },
            {
                component: "take",
                description: "This specifies the maximum number of rows to retrieve. " +
                    "Optional parameter. Defaults to 1 if missing"
            },
            {
                component: "skip",
                description: "This specifies the number of rows to skip before starting to retrieve rows. " +
                    "Optional parameter. Defaults to 0 if missing"
            },
        ],
        paginator: false
    };

    widgetSyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@widget(widget_code)",
                example: "... See the blood pressure chart below: @widget(chart_bp_by_days)",
                description: "Full syntax."
            },
        ],
        paginator: false
    };

    widgetSyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@widget",
                description: "This is the directive name indicating that the following argument " +
                    "is for a dynamic widget insertion."
            },
            {
                component: "widget_code",
                description: "This represents a string code of embedded widget. The code is defined " +
                    "in general settings of embedded widget's builder."
            },
        ],
        paginator: false
    };

    expressionSyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@eval(math_expression)",
                example: "@eval(#score1 + #score2)",
                description: "Full syntax."
            },
        ],
        paginator: false
    };

    expressionSyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@eval",
                description: "This is the directive name indicating that the following argument " +
                    "is an expression to be evaluated."
            },
            {
                component: "math_expression",
                description: "It can be any valid expression supported by the math.js library, " +
                    "including basic arithmetic and advanced functions."
            },
        ],
        paginator: false
    };

    forLoopSyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@for(variable of list) { body }",
                example: "@for(item of @query(getItems)) { <p>@item</p> }",
                description: "Basic for loop syntax. Iterates over a list and repeats the body for each item."
            },
            {
                syntax: "@for(variable of list; var1 = $index; var2 = $count) { body }",
                example: "@for(user of @query(getUsers); idx = $index; total = $count) " +
                    "{ <p>User @idx of @total: @user</p> }",
                description: "For loop with variable declarations. " +
                    "Available identifiers: $index, $count, $first, $last, $even, $odd."
            },
        ],
        paginator: false
    };

    forLoopSyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@for",
                description: "This is the directive name indicating a for loop iteration."
            },
            {
                component: "variable of list",
                description: "The loop variable name and the list expression to iterate over. " +
                    "Format: 'variableName of listExpression'."
            },
            {
                component: "var = $identifier",
                description: "Optional variable declarations separated by semicolons. " +
                    "Available identifiers: $index (0-based), $count (total items), " +
                    "$first (boolean), $last (boolean), $even (boolean), $odd (boolean)."
            },
            {
                component: "{ body }",
                description: "The body block that will be repeated for each iteration. " +
                    "Can contain HTML and other directives."
            },
        ],
        paginator: false
    };

    rangeSyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@range(count)",
                example: "@range(5)",
                description: "Creates a list of numbers from 0 to count-1. Returns [0, 1, 2, 3, 4] for count=5."
            },
            {
                syntax: "@range(start, end)",
                example: "@range(2, 5)",
                description: "Creates a list of numbers from start to end-1. Returns [2, 3, 4] for start=2, end=5."
            },
            {
                syntax: "@for(i of @range(count)) { body }",
                example: "@for(i of @range(3)) { <p>Item @i</p> }",
                description: "Combined with @for to iterate a specific number of times. Useful for repeating content."
            },
        ],
        paginator: false
    };

    rangeSyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@range",
                description: "This is the directive name that generates a list of sequential numbers."
            },
            {
                component: "count",
                description: "When used with a single argument, specifies the number of items to generate " +
                    "starting from 0. The list will contain numbers from 0 to count-1."
            },
            {
                component: "start",
                description: "When used with two arguments, specifies the starting number of the range. " +
                    "Required when end is specified."
            },
            {
                component: "end",
                description: "When used with two arguments, specifies the end number (exclusive). " +
                    "The list will contain numbers from start to end-1."
            },
        ],
        paginator: false
    };

    typeConversionSyntaxTableSettings: ITableSettings = {
        columns: this.syntaxColumns,
        value: [
            {
                syntax: "@string(value)",
                example: "@string(@query(getPrice))",
                description: "Converts the value to a string representation."
            },
            {
                syntax: "@number(value)",
                example: "@number(@query(getCount))",
                description: "Converts the value to a number. Returns 0 if conversion fails."
            },
            {
                syntax: "@bool(value)",
                example: "@bool(@query(getIsActive))",
                description: "Converts the value to a boolean. Returns false if conversion fails."
            },
        ],
        paginator: false
    };

    typeConversionSyntaxComponentsTableSettings: ITableSettings = {
        columns: this.syntaxComponentsColumns,
        value: [
            {
                component: "@string",
                description: "Directive that converts a value to its string representation, wrapped in quotes."
            },
            {
                component: "@number",
                description: "Directive that converts a value to a number. " +
                    "Returns 0 if the value cannot be converted."
            },
            {
                component: "@bool",
                description: "Directive that converts a value to a boolean. " +
                    "Returns false if the value cannot be converted."
            },
            {
                component: "value",
                description: "The value to convert. Can be any expression, query result, or other directive output."
            },
        ],
        paginator: false
    };
}
